import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, Copy, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { ConverterForm } from "./ConverterForm";
import { ConverterResult } from "./ConverterResult";
import { ShareMenu } from "./ShareMenu";
import { RecentConversions, pushRecent, readRecent } from "./RecentConversions";
import LiveClocks from "./LiveClocks";
import { useConverterState } from "./useConverterState";
import { copyText } from "./shareUtils";
import {
    buildShareUrl, buildPreviewShareUrl, buildDemoBookingPath, conversionWhatsAppMessage, resultText,
    converterLinkMessage,
} from "../../lib/timeConverter";

const btn = "h-11 inline-flex items-center justify-center gap-2 px-4 rounded-lg text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

/** Shared converter UI. variant="admin" adds WhatsApp message, link sharing and session history. */
export default function TimeConverter({ variant = "public", initial, defaultTz = "" }) {
    const admin = variant === "admin";
    const prefix = admin ? "tc-admin" : "tc-public";
    const c = useConverterState({ initial, defaultTz });
    const [recent, setRecent] = useState(() => (admin ? readRecent() : []));
    const ok = c.result.status === "ok";
    const params = { dir: c.dir, tz: c.tz, date: c.date, time: c.time, hour12: c.hour12 };
    const shareUrl = buildPreviewShareUrl(params);
    // Converter link keeps city + direction (+ 24h pref) so recipients see that city's live clock.
    const linkParams = c.tz ? { dir: c.dir, tz: c.tz, hour12: c.hour12 } : {};
    const converterUrl = buildPreviewShareUrl(linkParams);
    const booking = admin ? null : buildDemoBookingPath(c.result, c.tz);

    const remember = () => {
        if (!admin || !ok) return;
        const summary = resultText(c.result, c.place).replace("\n", "  ");
        setRecent(pushRecent({ key: `${c.dir}|${c.zone}|${c.date}|${c.time}`, dir: c.dir, tz: c.tz, date: c.date, time: c.time, summary }));
    };
    const copy = async (text, okMsg) => {
        if (await copyText(text)) { toast.success(okMsg); remember(); }
        else toast.error("Couldn't copy — please select and copy manually");
    };

    const conversionMsg = ok
        ? (admin
            ? conversionWhatsAppMessage(c.result, c.place, shareUrl)
            : `Here is the time conversion I checked:\n\n${resultText(c.result, c.place)}\n\nCompare here: ${shareUrl}`)
        : "";
    const shareConversionMsg = ok
        ? `Hello Sir/Ma'am,\n\nPlease check this demo timing in your local time and in India (IST):\n\n${resultText(c.result, c.place)}\n\nOpen or compare it here: ${shareUrl}\n\nThis is a calculated time only, not a confirmed booking. Our team will confirm the demo class.`
        : "";

    return (
        <div className="space-y-5" data-testid={`${prefix}-root`}>
            {admin && (
                <div className="flex flex-wrap items-center gap-2" data-testid="tc-admin-link-bar">
                    <ShareMenu label="Share Converter Link" url={converterUrl} message={converterLinkMessage(converterUrl)}
                        openHref={buildShareUrl(linkParams, "")} testId="tc-share-link" primary />
                    <span className="text-xs text-slate-500 break-all" data-testid="tc-public-url">{converterUrl}</span>
                </div>
            )}

            <LiveClocks tz={c.tz} place={c.place} hour12={c.hour12} />

            <div className="grid md:grid-cols-2 gap-5 items-start">
                <div className="min-w-0 bg-white border border-slate-200 rounded-2xl p-4 sm:p-5">
                    <ConverterForm c={c} prefix={prefix} />
                </div>
                <div className="min-w-0 bg-slate-50 border border-slate-200 rounded-2xl p-4 sm:p-5 space-y-4">
                    <ConverterResult c={c} prefix={prefix} />
                    <div className="flex flex-wrap gap-2">
                        <button type="button" disabled={!ok} onClick={() => copy(resultText(c.result, c.place), "Result copied")}
                            data-testid={`${prefix}-copy-result`} className={`${btn} bg-[#0F172A] text-white hover:bg-[#1E293B]`}>
                            <Copy className="w-4 h-4" /> Copy Result
                        </button>
                        {admin && (
                            <button type="button" disabled={!ok} onClick={() => copy(conversionMsg, "WhatsApp message copied")}
                                data-testid="tc-admin-copy-whatsapp" className={`${btn} bg-[#25D366] text-white hover:bg-[#1ebe5a]`}>
                                <MessageCircle className="w-4 h-4" /> Copy WhatsApp Message
                            </button>
                        )}
                        <ShareMenu label={admin ? "Share This Conversion" : "Share"} url={shareUrl}
                            message={admin ? shareConversionMsg : conversionMsg} disabled={!ok} onShared={remember}
                            openHref={admin ? buildShareUrl(params, "") : null} testId={`${prefix}-share-conversion`} />
                        {!admin && booking?.path && (
                            <Link to={booking.path} data-testid="tc-public-book-demo" className={`${btn} bg-amber-500 text-[#0F172A] hover:bg-amber-400`}>
                                <CalendarCheck className="w-4 h-4" /> Book this demo time
                            </Link>
                        )}
                    </div>
                    {!admin && booking?.tooSoon && (
                        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="tc-public-book-too-soon">
                            New demo classes need 24–48 hours to arrange. To book online, please choose a date from {new Date(`${booking.minDate}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })} (India date) onward.
                        </p>
                    )}
                </div>
            </div>

            {admin && (
                <RecentConversions items={recent} onReuse={(e) => c.load(e)}
                    onClear={() => { sessionStorage.removeItem("at_tc_recent"); setRecent([]); }} />
            )}
        </div>
    );
}
